import React, { useRef, useState } from 'react';
import { Booking } from '../types';
import { Interval } from './availability';
import { formatMinutes, ZonedTime } from './calendarTime';
import { resourceColor } from './layout';

export const PX_PER_MINUTE = 1.2;
const AXIS_WIDTH = 52;
const NARROW_WIDTH = 56;
const MIN_COLUMN_WIDTH = 120;
const MIN_LANE_WIDTH = 32;
const DRAG_THRESHOLD = 5;
const PAST_HATCH =
  'repeating-linear-gradient(135deg, rgba(100,116,139,0.28) 0, rgba(100,116,139,0.28) 1px, transparent 1px, transparent 7px)';

export interface GridLane {
  resourceId: string;
  name: string;
  colorIndex: number;
  inactive: boolean;
}

export interface GridColumn {
  key: string;
  date: string;
  // Day view: the column belongs to one resource. Week view: all resources of the day.
  resourceId: string | null;
  title: string;
  subtitle?: string;
  colorIndex?: number;
  // Day view: the column's resource is inactive (existing bookings stay visible).
  inactive?: boolean;
  narrow: boolean;
  isToday: boolean;
  // Union of the working hours; only used for the visible time range.
  intervals: Interval[];
  // Week view: every visible resource keeps a fixed lane inside the day.
  lanes: GridLane[];
  // Working hours per lane (same order as lanes), drawn inside that lane only.
  laneIntervals: Interval[][];
}

export interface GridBooking {
  booking: Booking;
  date: string;
  start: number;
  end: number;
  colorIndex: number;
  // Set when the appointment cannot take place as booked and has to be rescheduled.
  warning?: string;
}

export interface GridTarget {
  date: string;
  minutes: number;
  // Only set in the day view, where a drop can change the resource.
  resourceId: string | null;
}

interface DragState {
  id: string;
  columnKey: string;
  start: number;
  laneIndex: number;
}

interface Props {
  columns: GridColumn[];
  bookings: GridBooking[];
  startMinute: number;
  endMinute: number;
  granularity: number;
  // Current time in the profile's timezone; everything before it is hatched.
  now: ZonedTime;
  onCreate: (target: GridTarget) => void;
  onOpen: (booking: Booking) => void;
  onMove: (booking: Booking, target: GridTarget) => void;
}

export const CalendarGrid: React.FC<Props> = ({
  columns,
  bookings,
  startMinute,
  endMinute,
  granularity,
  now,
  onCreate,
  onOpen,
  onMove,
}) => {
  const columnRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pending = useRef<{
    id: string;
    x: number;
    y: number;
    grab: number;
    started: boolean;
  } | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  const height = (endMinute - startMinute) * PX_PER_MINUTE;
  const template = `${AXIS_WIDTH}px ${columns
    .map(column =>
      column.narrow
        ? `${NARROW_WIDTH}px`
        : `minmax(${Math.max(MIN_COLUMN_WIDTH, column.lanes.length * MIN_LANE_WIDTH)}px, 1fr)`
    )
    .join(' ')}`;
  const hours: number[] = [];
  for (let minute = startMinute; minute < endMinute; minute += 60) hours.push(minute);

  const snap = (minute: number) => Math.round(minute / granularity) * granularity;

  const columnAt = (clientX: number) =>
    columns.find(column => {
      const element = columnRefs.current[column.key];
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return clientX >= rect.left && clientX < rect.right;
    });

  const laneIndexAt = (column: GridColumn, clientX: number) => {
    const element = columnRefs.current[column.key];
    if (!element || column.lanes.length === 0) return 0;
    const rect = element.getBoundingClientRect();
    const index = Math.floor(((clientX - rect.left) / rect.width) * column.lanes.length);
    return Math.min(Math.max(index, 0), column.lanes.length - 1);
  };

  const resourceAt = (column: GridColumn, laneIndex: number) =>
    column.resourceId ?? column.lanes[laneIndex]?.resourceId ?? null;

  const isInactiveAt = (column: GridColumn, laneIndex: number) =>
    column.resourceId !== null ? !!column.inactive : !!column.lanes[laneIndex]?.inactive;

  // Week view: a thicker, darker line keeps the days apart from the thin lane lines.
  const dividerClass = (column: GridColumn) =>
    column.resourceId === null
      ? 'border-l-2 border-gray-400 dark:border-gray-500'
      : 'border-l border-gray-200 dark:border-gray-700';

  const handleColumnClick = (column: GridColumn, event: React.MouseEvent<HTMLDivElement>) => {
    if (column.narrow) return;
    const laneIndex = laneIndexAt(column, event.clientX);
    if (isInactiveAt(column, laneIndex)) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const raw = startMinute + (event.clientY - rect.top) / PX_PER_MINUTE;
    const floored = Math.floor(raw / granularity) * granularity;
    onCreate({
      date: column.date,
      minutes: Math.min(Math.max(floored, startMinute), endMinute - granularity),
      resourceId: resourceAt(column, laneIndex),
    });
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>, item: GridBooking) => {
    if (item.booking.status !== 'confirmed' || event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    pending.current = {
      id: item.booking.id,
      x: event.clientX,
      y: event.clientY,
      grab: (event.clientY - rect.top) / PX_PER_MINUTE,
      started: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>, item: GridBooking) => {
    const state = pending.current;
    if (!state || state.id !== item.booking.id) return;
    if (!state.started) {
      if (Math.hypot(event.clientX - state.x, event.clientY - state.y) < DRAG_THRESHOLD) return;
      state.started = true;
    }
    const column = columnAt(event.clientX);
    const element = column && columnRefs.current[column.key];
    if (!column || !element) return;
    const top = element.getBoundingClientRect().top;
    const raw = startMinute + (event.clientY - top) / PX_PER_MINUTE - state.grab;
    const duration = item.end - item.start;
    const next = {
      id: state.id,
      columnKey: column.key,
      start: Math.min(Math.max(snap(raw), startMinute), endMinute - duration),
      laneIndex: laneIndexAt(column, event.clientX),
    };
    dragRef.current = next;
    setDrag(next);
  };

  const finishPointer = (item: GridBooking, cancelled: boolean) => {
    const state = pending.current;
    const target = dragRef.current;
    pending.current = null;
    dragRef.current = null;
    setDrag(null);
    if (!state || state.id !== item.booking.id || cancelled) return;
    if (!state.started) {
      onOpen(item.booking);
      return;
    }
    const column = target && columns.find(candidate => candidate.key === target.columnKey);
    if (!target || !column) return;
    const resourceId = resourceAt(column, target.laneIndex);
    const sameResource = resourceId === null || resourceId === item.booking.resource;
    const unchanged = column.date === item.date && target.start === item.start;
    if (unchanged && sameResource) return;
    onMove(item.booking, {
      date: column.date,
      minutes: target.start,
      resourceId,
    });
  };

  const renderBlock = (
    item: GridBooking,
    style: React.CSSProperties,
    options: { ghost?: boolean; faded?: boolean; lanes?: number }
  ) => {
    const { booking } = item;
    const color = resourceColor(item.colorIndex);
    const short = (item.end - item.start) * PX_PER_MINUTE < 40;
    // Many lanes leave little width: drop the service line, then the time.
    const lanes = options.lanes ?? 1;
    const compact = lanes > 4;
    const tiny = lanes > 8;
    const movable = booking.status === 'confirmed';
    return (
      <div
        key={`${booking.id}${options.ghost ? '-ghost' : ''}`}
        role="button"
        tabIndex={0}
        title={`${formatMinutes(item.start)}–${formatMinutes(item.end)} ${booking.customer_name} · ${booking.service_name}${booking.variant_name ? ` (${booking.variant_name})` : ''} · ${booking.resource_name}${item.warning ? `\n⚠ ${item.warning}` : ''}`}
        className={`absolute overflow-hidden rounded border-l-4 py-0.5 text-xs leading-tight shadow-sm ${
          compact ? 'px-0.5' : 'px-1.5'
        } ${color.block} ${
          movable ? 'cursor-grab touch-none active:cursor-grabbing' : 'cursor-pointer opacity-70'
        } ${options.faded ? 'opacity-40' : ''} ${options.ghost ? 'z-20 opacity-90 ring-2 ring-blue-500' : 'z-10'} ${
          item.warning && !options.ghost ? 'ring-2 ring-red-500' : ''
        } ${
          booking.status === 'no_show' ? 'line-through' : ''
        }`}
        style={style}
        onClick={event => {
          event.stopPropagation();
          if (!movable && !options.ghost) onOpen(booking);
        }}
        onKeyDown={event => {
          if (event.key === 'Enter') onOpen(booking);
        }}
        onPointerDown={event => !options.ghost && handlePointerDown(event, item)}
        onPointerMove={event => !options.ghost && handlePointerMove(event, item)}
        onPointerUp={event => {
          if (!options.ghost) {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              event.currentTarget.releasePointerCapture(event.pointerId);
            }
            finishPointer(item, false);
          }
        }}
        onPointerCancel={() => !options.ghost && finishPointer(item, true)}
      >
        <div className="font-semibold truncate">
          {tiny
            ? booking.customer_name
            : compact
              ? formatMinutes(item.start)
              : short
                ? `${formatMinutes(item.start)} ${booking.customer_name}`
                : `${formatMinutes(options.ghost && drag ? drag.start : item.start)}–${formatMinutes(
                    (options.ghost && drag ? drag.start : item.start) + (item.end - item.start)
                  )}`}
        </div>
        {!tiny && !short && <div className="truncate">{booking.customer_name}</div>}
        {!compact && !short && <div className="truncate opacity-80">{booking.service_name}</div>}
      </div>
    );
  };

  const draggedItem = drag ? bookings.find(item => item.booking.id === drag.id) : undefined;

  return (
    <div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <div style={{ minWidth: 'max-content' }}>
        <div
          className="sticky top-0 z-30 grid border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
          style={{ gridTemplateColumns: template }}
        >
          <div />
          {columns.map(column => (
            <div
              key={column.key}
              className={`${dividerClass(column)} px-2 py-2 text-center ${
                column.isToday ? 'bg-blue-50 dark:bg-blue-900/20' : ''
              }`}
            >
              <div className="flex items-center justify-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
                {column.colorIndex !== undefined && (
                  <span
                    className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${resourceColor(column.colorIndex).dot}`}
                  />
                )}
                <span className={`truncate ${column.inactive ? 'opacity-60' : ''}`}>
                  {column.title}
                </span>
              </div>
              {column.subtitle && !column.narrow && (
                <div className="text-xs text-gray-500 dark:text-gray-400">{column.subtitle}</div>
              )}
              {!column.narrow && column.lanes.length > 1 && (
                <div className="mt-1 flex gap-px">
                  {column.lanes.map(lane => (
                    <span
                      key={lane.resourceId}
                      title={lane.name}
                      className={`min-w-0 flex-1 truncate rounded-sm text-[10px] font-semibold leading-4 text-white ${
                        resourceColor(lane.colorIndex).dot
                      } ${lane.inactive ? 'opacity-40' : ''}`}
                    >
                      {lane.name.charAt(0).toUpperCase()}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="grid" style={{ gridTemplateColumns: template }}>
          <div className="relative" style={{ height }}>
            {hours.map(minute => (
              <div
                key={minute}
                className="absolute right-1 -translate-y-1/2 text-xs text-gray-400"
                style={{ top: (minute - startMinute) * PX_PER_MINUTE }}
              >
                {minute === startMinute ? '' : formatMinutes(minute)}
              </div>
            ))}
          </div>

          {columns.map(column => {
            const laneCount = Math.max(column.lanes.length, 1);
            const placed = bookings
              .filter(
                item =>
                  item.date === column.date &&
                  (column.resourceId === null || item.booking.resource === column.resourceId)
              )
              .map(item => ({
                item,
                lane:
                  column.resourceId === null
                    ? Math.max(
                        column.lanes.findIndex(lane => lane.resourceId === item.booking.resource),
                        0
                      )
                    : 0,
              }));
            const openAreas =
              column.lanes.length > 0
                ? column.laneIntervals.flatMap((intervals, lane) =>
                    intervals.map(([from, to]) => ({ lane, from, to }))
                  )
                : column.intervals.map(([from, to]) => ({ lane: 0, from, to }));
            const pastUntil =
              column.date < now.date ? endMinute : column.date === now.date ? now.minutes : 0;
            const pastHeight =
              (Math.min(Math.max(pastUntil, startMinute), endMinute) - startMinute) * PX_PER_MINUTE;
            const showNow =
              column.date === now.date && now.minutes >= startMinute && now.minutes < endMinute;
            return (
              <div
                key={column.key}
                ref={element => {
                  columnRefs.current[column.key] = element;
                }}
                className={`relative ${dividerClass(column)} ${
                  column.narrow
                    ? 'bg-gray-100 dark:bg-gray-800/70'
                    : `${column.inactive ? '' : 'cursor-pointer '}bg-gray-100 dark:bg-gray-800/70`
                }`}
                style={{
                  height,
                  backgroundImage:
                    'linear-gradient(to bottom, rgba(128,128,128,0.25) 1px, transparent 1px)',
                  backgroundSize: `100% ${60 * PX_PER_MINUTE}px`,
                }}
                onClick={event => handleColumnClick(column, event)}
                role="presentation"
              >
                {openAreas.map(({ lane, from, to }) => (
                  <div
                    key={`${lane}-${from}-${to}`}
                    className="pointer-events-none absolute bg-white dark:bg-gray-900"
                    style={{
                      left: `${(lane / laneCount) * 100}%`,
                      width: `${100 / laneCount}%`,
                      top: (Math.max(from, startMinute) - startMinute) * PX_PER_MINUTE,
                      height:
                        (Math.min(to, endMinute) - Math.max(from, startMinute)) * PX_PER_MINUTE,
                      backgroundImage:
                        'linear-gradient(to bottom, rgba(128,128,128,0.25) 1px, transparent 1px)',
                      backgroundSize: `100% ${60 * PX_PER_MINUTE}px`,
                      backgroundPositionY: `${-((Math.max(from, startMinute) - startMinute) * PX_PER_MINUTE)}px`,
                    }}
                  />
                ))}
                {pastHeight > 0 && (
                  <div
                    className="pointer-events-none absolute inset-x-0 top-0"
                    style={{ height: pastHeight, backgroundImage: PAST_HATCH }}
                  />
                )}
                {Array.from({ length: laneCount - 1 }, (_, index) => (
                  <div
                    key={`lane-${index}`}
                    className="pointer-events-none absolute inset-y-0 border-l border-gray-200/70 dark:border-gray-700/70"
                    style={{ left: `${((index + 1) / laneCount) * 100}%` }}
                  />
                ))}
                {placed.map(({ item, lane }) =>
                  renderBlock(
                    item,
                    {
                      top: (item.start - startMinute) * PX_PER_MINUTE,
                      height: Math.max((item.end - item.start) * PX_PER_MINUTE, 18),
                      left: `calc(${(lane / laneCount) * 100}% + 2px)`,
                      width: `calc(${100 / laneCount}% - 4px)`,
                    },
                    { faded: drag?.id === item.booking.id, lanes: column.lanes.length }
                  )
                )}
                {showNow && (
                  <div
                    className="pointer-events-none absolute inset-x-0 z-20 border-t-2 border-red-500"
                    style={{ top: (now.minutes - startMinute) * PX_PER_MINUTE }}
                  >
                    <span className="absolute -left-1 -top-[5px] h-2 w-2 rounded-full bg-red-500" />
                  </div>
                )}
                {draggedItem &&
                  drag?.columnKey === column.key &&
                  renderBlock(
                    draggedItem,
                    {
                      top: (drag.start - startMinute) * PX_PER_MINUTE,
                      height: Math.max((draggedItem.end - draggedItem.start) * PX_PER_MINUTE, 18),
                      left: `calc(${(drag.laneIndex / laneCount) * 100}% + 2px)`,
                      width: `calc(${100 / laneCount}% - 4px)`,
                      pointerEvents: 'none',
                    },
                    { ghost: true, lanes: column.lanes.length }
                  )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
